import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import KeycloakService from '../../services/KeycloakService';
import { getUserProjects } from '../../services/UserService';
import {
  createWeekendRequest,
  createWeekendTask,
  deleteWeekendTask,
  getMyWeekendRequests,
  getMyWeekendTasks,
} from '../../services/WeekendWorkingService';
import PaginationComponent from '../../components/Pagination/Pagination';
import { ERROR_KEY, SHOW_NOTIFICATION, SUCCESS_KEY } from '../../constants';
import WeekendRequestDialog from './WeekendRequestDialog';
import WeekendTaskDialog from './WeekendTaskDialog';
import { LabelledText, WeekendCard } from './WeekendCard';
import {
  STATUS_COLOR,
  STATUS_LABEL,
  formatDate,
  lastDecision,
  pendingFrom,
  datesText,
  toMoment,
} from './weekendUtils';

const PAGE_SIZE = 5;
const YYYY_MM_DD = 'YYYY-MM-DD';
const HOUR_MS = 3600 * 1000;

const hoursText = (ms) => `${Number(((ms || 0) / HOUR_MS).toFixed(2))} hrs`;

/** Employee view: ask for weekend dates, follow the decision, log hours on approved dates. */
const WeekendWorkingComponent = () => {
  const dispatch = useDispatch();
  const currentUserId = KeycloakService.getUserDetail();
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [taskTarget, setTaskTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const notify = useCallback(
    (type, message) =>
      dispatch({
        type: SHOW_NOTIFICATION,
        data: { open: true, type, message },
      }),
    [dispatch],
  );

  const loadRequests = useCallback(async () => {
    const res = await getMyWeekendRequests(currentUserId, {
      status,
      pageNumber: page - 1,
      pageSize: PAGE_SIZE,
    });
    if (res.isError === false) {
      setRequests(res.data?.data?.content || []);
      setTotalPages(Math.max(res.data?.data?.totalPages || 1, 1));
    } else {
      notify(res.type || ERROR_KEY, res.message);
    }
  }, [currentUserId, status, page, notify]);

  const loadTasks = useCallback(async () => {
    const res = await getMyWeekendTasks(currentUserId);
    if (res.isError === false) setTasks(res.data?.data?.content || []);
  }, [currentUserId]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    loadTasks();
    (async () => {
      const res = await getUserProjects(currentUserId, '', 100, 0, 'ACTIVE');
      if (res.isError === false) setProjects(res.data?.content || []);
    })();
  }, [currentUserId, loadTasks]);

  const tasksByDate = useMemo(() => {
    const byDate = {};
    tasks.forEach((task) => {
      const key = toMoment(task.workDate).format(YYYY_MM_DD);
      (byDate[key] = byDate[key] || []).push(task);
    });
    return byDate;
  }, [tasks]);

  const submitRequest = async ({ projectId, dates, reason }) => {
    setSubmitting(true);
    const res = await createWeekendRequest({
      employeeId: currentUserId,
      projectId,
      dates,
      reason,
    });
    setSubmitting(false);
    if (res.isError === false) {
      notify(
        SUCCESS_KEY,
        'Weekend working request sent to your project manager',
      );
      setRequestOpen(false);
      setPage(1);
      loadRequests();
      return true;
    }
    notify(res.type || ERROR_KEY, res.message);
    return false;
  };

  const submitTask = async ({ title, description, hours }) => {
    setSubmitting(true);
    const date = toMoment(taskTarget.date).format(YYYY_MM_DD);
    const res = await createWeekendTask({
      employeeId: currentUserId,
      workDate: date,
      projectId: taskTarget.projectId,
      title,
      description,
      requestedHours: Math.round(hours * HOUR_MS),
    });
    setSubmitting(false);
    if (res.isError === false) {
      notify(
        SUCCESS_KEY,
        'Weekend task sent to your project manager for approval',
      );
      setTaskTarget(null);
      loadTasks();
    } else {
      notify(res.type || ERROR_KEY, res.message);
    }
  };

  const removeTask = async (task) => {
    if (!window.confirm('Delete this weekend task?')) return;
    const res = await deleteWeekendTask(task.id);
    if (res.isError === false) {
      notify(SUCCESS_KEY, 'Weekend task deleted');
      loadTasks();
    } else {
      notify(res.type || ERROR_KEY, res.message);
    }
  };

  const renderDate = (request, date) => {
    const day = toMoment(date);
    const key = day.format(YYYY_MM_DD);
    const dayTasks = (tasksByDate[key] || []).filter(
      (task) => task.projectId === request.projectId,
    );
    const activeTasks = dayTasks.filter(
      (task) => task.status === 'PENDING' || task.status === 'APPROVED',
    );
    const hasProjectTask = activeTasks.some(
      (task) => task.projectId === request.projectId,
    );
    const canAdd =
      request.status === 'APPROVED' &&
      !day.isAfter(moment(), 'day') &&
      !hasProjectTask;
    return (
      <Box key={key} sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontWeight: 600, minWidth: 170 }}>
            {day.format('ddd, DD MMM YYYY')}
          </Typography>
          {request.status === 'APPROVED' && day.isAfter(moment(), 'day') && (
            <Typography variant="caption" color="text.secondary">
              Tasks can be added from this date.
            </Typography>
          )}
          {canAdd && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{ ml: 'auto' }}
              onClick={() =>
                setTaskTarget({
                  date: key,
                  projectId: request.projectId,
                  projectName: request.projectName,
                })
              }
            >
              Add task
            </Button>
          )}
        </Box>
        {request.status === 'APPROVED' &&
          dayTasks.map((task) => (
            <Box
              key={task.id}
              sx={{
                mt: 1,
                ml: 2,
                pl: 1.5,
                borderLeft: '3px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                  {task.title}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color="secondary"
                  label={hoursText(task.requestedHours)}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color={STATUS_COLOR[task.status]}
                  label={STATUS_LABEL[task.status] || task.status}
                />
                {task.status === 'PENDING' && (
                  <Tooltip title="Delete task">
                    <IconButton size="small" onClick={() => removeTask(task)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {task.description && (
                <Typography variant="body2" color="text.secondary">
                  {task.description}
                </Typography>
              )}
              {pendingFrom(task) && (
                <Typography variant="caption" color="warning.main">
                  Pending approval from {pendingFrom(task)}
                </Typography>
              )}
              {task.status === 'REJECTED' && task.comment && (
                <Typography variant="caption" color="error">
                  Rejected by {task.decidedByName || 'manager'}: {task.comment}
                </Typography>
              )}
            </Box>
          ))}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ mr: 'auto' }}>
          Weekend Working Hours
        </Typography>
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          {Object.keys(STATUS_LABEL).map((key) => (
            <MenuItem key={key} value={key}>
              {STATUS_LABEL[key]}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setRequestOpen(true)}
        >
          Request weekend work
        </Button>
      </Box>

      {requests.length === 0 && (
        <Typography color="text.secondary">
          No weekend working requests yet.
        </Typography>
      )}

      {requests.map((request) => {
        const decision = lastDecision(request);
        const dates = request.dates || [];
        const dateText = datesText(dates);
        return (
          <WeekendCard
            key={request.id}
            title={request.projectName}
            dateText={dateText}
            status={request.status}
          >
            {pendingFrom(request) && (
              <LabelledText label="Pending approval from">
                {pendingFrom(request)}
              </LabelledText>
            )}
            {dates.map((date) => renderDate(request, date))}
            {request.reason && (
              <LabelledText label="Reason">{request.reason}</LabelledText>
            )}
            {decision && (
              <LabelledText label="Decision">
                {STATUS_LABEL[decision.status]} by{' '}
                {decision.managerName || 'manager'}
                {decision.createdAt
                  ? ` on ${formatDate(decision.createdAt)}`
                  : ''}
                {decision.comment ? ` — ${decision.comment}` : ''}
              </LabelledText>
            )}
          </WeekendCard>
        );
      })}

      {totalPages > 1 && (
        <Box sx={{ mt: 2 }}>
          <PaginationComponent
            count={totalPages}
            page={page}
            pageChange={(e, value) => setPage(value)}
          />
        </Box>
      )}

      <WeekendRequestDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        onSubmit={submitRequest}
        projects={projects}
        submitting={submitting}
      />
      <WeekendTaskDialog
        open={Boolean(taskTarget)}
        onClose={() => setTaskTarget(null)}
        onSubmit={submitTask}
        date={taskTarget?.date}
        projectName={taskTarget?.projectName}
        submitting={submitting}
      />
    </Box>
  );
};

export default WeekendWorkingComponent;
